/**
 * Application shell: sidebar, top bar, notification panel, toast host.
 *
 * Replaces the fixed markup that lived at the top of the legacy index.html plus
 * the `.role-doctor` / `.role-admin` / `.role-patient` class-toggling that four
 * separate login paths performed by hand. The router decides what renders; the
 * shell just frames it.
 *
 * The chrome is the legacy chrome: the platform's mark heading the sidebar,
 * the agency mark in the header (both replaceable by the Super Admin — see
 * BrandMarks.jsx), colour-coded nav rows, the cyan bar down the
 * active link, and the pulsing refresh button. The rebuild had reduced all of
 * that to one grey column — everything still worked, but the product had lost
 * its face, and a menu of eight identical rows is slower to read than eight
 * coloured ones.
 */

import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon, Avatar, Spinner } from '../common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { LockedFeature } from '../../features/doctor/HospitalRestriction';
import {
  navItemsFor,
  navKey,
  NAV_TINTS,
  NAV_SECTION_LABEL,
  ROLE_LABELS,
  PLATFORM_HOSPITAL_ROWS,
  permittedNavItems,
} from './navigation';
import { actingInstitution } from '../../api/client';
import { ROLES } from '../../context/AuthContext';
import { NotificationPanel } from './NotificationPanel';
import { PasswordChangePrompt } from '../../features/settings/ChangePassword';
import { ToastHost } from './ToastHost';
import { AgencyMark, ProductMark } from './BrandMarks';
import { HelpAssistant } from '../../features/help/HelpAssistant';
import { MedicineAlarmHost } from '../../features/patient/MedicineAlarmHost';
import { ConsentGate } from '../../features/auth/ConsentGate';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';
import { useI18n } from '../../i18n/I18nProvider';
import { useMediaQuery } from '../../hooks/useLocalStorage';
import { pickLogo } from '../../domain/logoBackdrop';
import { isDarkTheme } from '../../domain/themes';
import { useTheme } from '../../context/ThemeContext';
import { LIMIT_EVENT } from '../../api/client';
import {
  METRIC_LABELS,
  STATE_LABELS,
  bannerFor,
  controlForPath,
  limitToast,
  stateOf,
  visibleNavItems,
} from '../../domain/serviceControl';

/** Hide a logo that fails to load rather than leaving a broken-image box. */
const hideOnError = (event) => {
  event.currentTarget.style.display = 'none';
};

export function AppShell() {
  const { session, role, logout, doctorProfile } = useAuth();
  const { refreshAll, refreshing } = useData();
  const { unread, panelOpen, togglePanel } = useNotifications();
  const permissions = usePermissions();
  const { roleTitle, institutionLogo, institutionLogoDark, institutionLogoBackdrop, institutionName, control } = permissions;
  const { theme } = useTheme();
  const { warn } = useNotifications();
  // Every label on the frame reads in the chosen language; the screens written
  // before المرحلة 1.1 pass their Arabic through tr() (i18n/I18nProvider.jsx).
  const { t, tr } = useI18n();
  const location = useLocation();
  // المرحلة 1.35: the institution's dark mark under a dark theme, when it has one.
  const shellLogo = pickLogo({ logo: institutionLogo, logoDark: institutionLogoDark, backdrop: institutionLogoBackdrop }, { dark: isDarkTheme(theme) });
  const navigate = useNavigate();
  const roleLabel = tr(roleTitle || ROLE_LABELS[role] || '');

  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The Super Admin working inside a hospital: which one, kept in sync with
  // the header the API client sends (see api/client.js actingInstitution).
  const [acting, setActing] = useState(() => (role === ROLES.ADMIN ? actingInstitution.get() : null));
  useEffect(() => {
    const sync = () => setActing(role === ROLES.ADMIN ? actingInstitution.get() : null);
    sync();
    window.addEventListener('capsule:acting-institution', sync);
    return () => window.removeEventListener('capsule:acting-institution', sync);
  }, [role]);

  const leaveHospital = () => {
    actingInstitution.clear();
    navigate('/admin/institutions');
  };

  // The central control: a desk the package has closed is not in the menu.
  // And a row the account lacks the capability for is not either (المرحلة 1.1:
  // a manager on the finance role sees the finance rows).
  const navItems = permittedNavItems(
    visibleNavItems(acting ? [...navItemsFor(role), ...PLATFORM_HOSPITAL_ROWS] : navItemsFor(role), control),
    permissions,
  );

  // A request that brought a limit to 80% or more comes back with a header
  // (api/client.js); say so once, in plain words.
  useEffect(() => {
    const onLimit = (event) => {
      const text = limitToast(event.detail, METRIC_LABELS);
      if (text) warn(text);
    };
    window.addEventListener(LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(LIMIT_EVENT, onLimit);
  }, [warn]);
  const banner = bannerFor(control, controlForPath(location.pathname));
  const sectionLabel = NAV_SECTION_LABEL[role] ? tr(NAV_SECTION_LABEL[role]) : null;
  const displayName = session?.name || t('shell.user');
  const avatarSrc = doctorProfile?.image || session?.picture || null;

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex shrink-0 items-center gap-3 border-b border-white/5 p-5">
        {/* The platform's mark — the one the Super Admin uploaded, if any. */}
        <ProductMark variant="sidebar" />
        {/* The hospital's own mark, beside the platform's — never instead of
            it. Set from the administrative manager's dashboard. */}
        {shellLogo.src && (
          <img
            src={shellLogo.src}
            alt={institutionName ? t('shell.logoOf', { name: institutionName }) : t('shell.hospitalLogo')}
            title={institutionName || undefined}
            onError={hideOnError}
            className={`hospital-logo h-[4.5rem] w-[4.5rem] shrink-0 rounded-2xl object-contain p-0.5 ${shellLogo.plate}`}
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="custom-scrollbar flex-1 space-y-2 overflow-y-auto p-4">
        {sectionLabel && (
          <p className="px-2 pb-1 text-xs font-semibold text-slate-500">
            {sectionLabel}
          </p>
        )}

        {navItems.map((item) =>
          // A heading row splits one menu into sections — an admin sees the
          // admin tools, then their own patient tabs under a divider.
          item.section ? (
            <p
              key={navKey(item)}
              className="px-2 pb-1 pt-4 text-xs font-semibold text-slate-500"
            >
              {tr(item.section)}
            </p>
          ) : (
            <SidebarLink
              key={navKey(item)}
              item={item}
              state={stateOf(control, controlForPath(item.to))}
              onNavigate={() => setDrawerOpen(false)}
            />
          ),
        )}
      </nav>

      {/* Who is signed in, and the way out */}
      <div className="shrink-0 border-t border-white/5 p-4">
        <div className="mb-3 flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-3">
          <Avatar src={avatarSrc} name={displayName} size={40} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{displayName}</p>
            <p className="truncate text-xs text-slate-400">{roleLabel}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/[0.08] px-4 py-2.5 text-sm font-medium text-red-300/80 transition-all hover:border-red-500/40 hover:bg-red-500/[0.18] hover:text-red-300"
        >
          <Icon name="log-out" size={16} />
          {t('shell.logout')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-backdrop flex min-h-screen">
      {/* Desktop sidebar */}
      {isDesktop && (
        <aside
          className="sticky top-0 h-screen w-72 shrink-0 border-e border-white/[0.06]"
          style={{
            background: 'var(--capsule-sidebar)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          {sidebar}
        </aside>
      )}

      {/* Mobile drawer */}
      {!isDesktop && drawerOpen && (
        <div className="fixed inset-0 z-[150] flex">
          <div
            className="flex-1 bg-black/70"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="h-full w-72 border-e border-white/5 bg-slate-950">{sidebar}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-[100] flex items-center gap-3 border-b border-white/5 bg-slate-950/80 px-4 py-3 backdrop-blur">
          {!isDesktop && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="rounded-lg p-2 text-slate-300 hover:bg-white/5"
              aria-label={t('shell.openMenu')}
            >
              <Icon name="menu" size={20} />
            </button>
          )}

          {/* The agency mark, as it appeared across every legacy dashboard —
              or the one the Super Admin uploaded in its place */}
          <AgencyMark variant="header" className="hidden sm:block" />

          {shellLogo.src && (
            <img
              src={shellLogo.src}
              alt={institutionName ? t('shell.logoOf', { name: institutionName }) : t('shell.hospitalLogo')}
              onError={hideOnError}
              className={`hospital-logo h-11 w-11 shrink-0 rounded-xl object-contain p-0.5 sm:h-[3.25rem] sm:w-[3.25rem] ${shellLogo.plate}`}
            />
          )}

          <div className="min-w-0 flex-1 text-end">
            <p className="truncate text-sm font-bold text-white">{displayName}</p>
            <p className="truncate text-[11px] text-slate-400">{roleLabel}</p>
          </div>

          {/* ع | EN — only where the institution's policy offers both */}
          <LanguageSwitch compact={!isDesktop} className="shrink-0" />

          {/* Smart refresh — glows on its own, spins while it works */}
          <button
            type="button"
            onClick={refreshAll}
            disabled={refreshing}
            title={t('shell.refreshTitle')}
            aria-label={t('shell.refresh')}
            className="smart-refresh-btn group shrink-0 rounded-xl p-2.5 disabled:cursor-wait"
          >
            <Icon
              name="rotate-cw"
              size={20}
              className={`text-cyan-400 transition-colors group-hover:text-cyan-300 ${
                refreshing ? 'animate-spin' : ''
              }`}
            />
          </button>

          <button
            type="button"
            onClick={togglePanel}
            className="group relative shrink-0 rounded-xl p-2.5 text-slate-400 transition-colors hover:bg-white/5"
            aria-label={`${t('shell.notifications')}${unread > 0 ? ` (${t('shell.unread', { n: unread })})` : ''}`}
          >
            <Icon
              name="bell"
              size={20}
              className="transition-colors group-hover:text-cyan-400"
            />
            {unread > 0 && (
              <span className="absolute -start-0.5 -top-0.5 flex h-[18px] min-w-[18px] animate-pulse items-center justify-center rounded-full border-2 border-[color:var(--capsule-bg)] bg-gradient-to-br from-red-500 to-rose-600 px-1 text-[10px] font-black text-onaccent shadow-lg shadow-red-500/40">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          <Avatar src={avatarSrc} name={displayName} size={36} />
        </header>

        {acting && (
          <div className="acting-banner flex flex-wrap items-center gap-3 border-b border-amber-500/30 bg-gradient-to-l from-amber-500/15 via-orange-500/10 to-transparent px-4 py-2.5 text-sm">
            <span className="tile3d__face tone-amber shrink-0" style={{ width: 34, height: 34 }}>
              <Icon name="hospital" size={18} className="tile3d__icon" />
            </span>
            <p className="min-w-0 flex-1 text-amber-100">
              {t('shell.actingIn')} <b className="text-white">{acting.name || `#${acting.id}`}</b> {t('shell.actingSuffix')}
            </p>
            <button type="button" onClick={leaveHospital} className="btn-ghost !py-1.5 text-xs">
              <Icon name="log-out" size={14} /> {t('shell.leaveHospital')}
            </button>
          </div>
        )}

        <main className="min-w-0 flex-1 p-4 md:p-6">
          {banner && (
            <div className={`service-banner tone-${banner.tone} mb-5 flex items-start gap-3 rounded-2xl p-4`} role="status">
              <span className="tile3d__face shrink-0" style={{ width: 38, height: 38 }}>
                <Icon name={banner.icon} size={19} className="tile3d__icon" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">{tr(banner.title)}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-300">{tr(banner.text)}</p>
              </div>
            </div>
          )}
          {/* The department desks load as their own chunks (app/router.jsx);
              only the content area waits for one, never the menu around it. */}
          <Suspense fallback={<Spinner label={t('shell.opening')} variant="inline" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {panelOpen && <NotificationPanel />}
      <PasswordChangePrompt />
      {/* A consent text the person has not accepted (or that changed since): asked before anything else */}
      <ConsentGate />
      {/* The help assistant and support, on every screen. */}
      <HelpAssistant />
      {/* A patient's dose alarm rings on every screen, not only «أدويتي». */}
      {(role === ROLES.PATIENT || role === ROLES.TEAM_MEMBER) && <MedicineAlarmHost />}
      <ToastHost />
    </div>
  );
}

/**
 * One nav row.
 *
 * Three shapes, all from the legacy sidebar:
 *   - `tint`     the whole row is coloured (the admin menu)
 *   - `icontint` only the glyph is coloured (doctor and patient menus)
 *   - `pulse`    a live dot replaces the glyph (عرض Live)
 */
function SidebarLink({ item, state = 'open', onNavigate }) {
  const tint = item.tint ? NAV_TINTS[item.tint] : null;
  const iconTint = item.icontint ? NAV_TINTS[item.icontint] : null;

  // A row the account lacks the capability for stays in the menu, greyed, and
  // opens the upsell instead of navigating. The requirement is explicit that a
  // hospital doctor must still SEE the private-practice features — removing the
  // row would mean they never learn the product exists. See
  // features/doctor/HospitalRestriction.jsx.
  const { can, loading: permsLoading } = usePermissions();
  const { tr } = useI18n();
  const label = tr(item.label);
  const locked = Boolean(item.locks) && !permsLoading && !can(item.locks);

  if (locked) {
    return (
      <LockedFeature feature={label}>
        <div
          className={[
            'sidebar-link group flex items-center gap-3 rounded-xl px-4 py-3 text-sm',
            tint ? `tinted ${tint.bg} ${tint.border} ${tint.text} font-bold` : 'text-slate-300',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Icon
            name={item.icon}
            size={20}
            className={tint ? tint.icon : iconTint ? iconTint.icon : 'text-slate-400'}
          />
          <span className="flex-1">{label}</span>
        </div>
      </LockedFeature>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'sidebar-link group flex items-center gap-3 rounded-xl px-4 py-3 text-sm',
          tint ? `tinted ${tint.bg} ${tint.border} ${tint.text} font-bold` : 'text-slate-300',
          isActive ? 'active' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
    >
      {item.pulse ? (
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
      ) : (
        <Icon
          name={item.icon}
          size={20}
          className={
            tint
              ? tint.icon
              : iconTint
                ? iconTint.icon
                : 'text-slate-400 transition-colors group-hover:text-cyan-400'
          }
        />
      )}

      <span
        className={
          tint ? 'flex-1' : 'flex-1 transition-colors group-hover:text-white'
        }
      >
        {label}
      </span>
      {/* The central control: a desk that is read-only or being closed says
          so in the menu, before the screen does. */}
      {['readonly', 'draining', 'trial'].includes(state) && (
        <span className={`nav-state nav-state--${state}`} title={tr(STATE_LABELS[state])}>
          {tr(STATE_LABELS[state])}
        </span>
      )}
    </NavLink>
  );
}
